/**
 * KNK-1.52 / FR-42 — activating Knock Mode over an account's existing pins.
 *
 * The decisions and copy, with no React. The data is migration 20261009a6:
 *   knock_activation_preview()  a dry run of the import, plus the run log
 *   knock_activation_apply(n)   the import; `n` is the previewed Knock count
 *   knock_activation_revert(id) undo the latest run while nothing has happened since
 *
 * ⚠️ THIS IS NOT THE GATE. The database refuses everything this screen hides: only
 * `field.settings.manage` may run it, only while Knock Mode is on, and whether a run can still be
 * undone is the server's `revert_blocker`, never worked out here.
 */

export interface ActivationRun {
  id: string;
  appliedAt: string;
  runByName: string | null;
  knocksImported: number;
  doorsTouched: number;
  duplicatesMerged: number;
  doorsRecolored: number;
  repsNotified: number;
  revertedAt: string | null;
  knocksRemoved: number | null;
  /** The server's sentence for why it cannot be undone, or null when it can. */
  revertBlocker: string | null;
  undoable: boolean;
}

export interface ActivationPreview {
  knocksImported: number;
  doorsTouched: number;
  duplicatesMerged: number;
  doorsRecolored: number;
  rowsSkipped: number;
  doors: number;
  repsToNotify: number;
  runs: ActivationRun[];
}

export const ACTIVATION_COPY = {
  title: 'Bring in existing pins',
  hint: 'Turns every pin status change your team has already recorded into a Knock, so each Door keeps its history once reps start knocking.',
  preview: 'Preview',
  apply: 'Activate',
  undo: 'Undo',
  nothingToImport: 'Nothing to bring in: every pin status change is already a Knock.',
  alreadyTold: 'Reps were already told the team sees Door colors on an earlier run.',
  notesPrivate: 'Notes and homeowner details are not copied. They stay with the people on each Lead.',
  runsTitle: 'Runs',
  noRuns: 'Not run yet.',
  undoHint: 'A run can be undone until the first new Knock or pin status change after it.',
  applied: 'Existing pins brought in',
  undone: 'Run undone',
} as const;

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const str = (v: unknown): string | null => (typeof v === 'string' && v.length > 0 ? v : null);
const int = (v: unknown): number | null => (typeof v === 'number' && Number.isInteger(v) && v >= 0 ? v : null);

export function parseActivationRuns(raw: unknown): ActivationRun[] {
  if (!Array.isArray(raw)) return [];
  const runs: ActivationRun[] = [];
  for (const r of raw) {
    if (!isRecord(r)) continue;
    const id = str(r.id);
    const appliedAt = str(r.applied_at);
    const knocksImported = int(r.knocks_imported);
    if (!id || !appliedAt || knocksImported === null) continue;
    const revertBlocker = str(r.revert_blocker);
    const revertedAt = str(r.reverted_at);
    runs.push({
      id,
      appliedAt,
      runByName: str(r.run_by_name),
      knocksImported,
      doorsTouched: int(r.doors_touched) ?? 0,
      duplicatesMerged: int(r.duplicates_merged) ?? 0,
      doorsRecolored: int(r.doors_recolored) ?? 0,
      repsNotified: int(r.reps_notified) ?? 0,
      revertedAt,
      knocksRemoved: int(r.knocks_removed),
      revertBlocker,
      undoable: revertBlocker === null && revertedAt === null,
    });
  }
  return runs;
}

export function parseActivationPreview(raw: unknown): ActivationPreview | null {
  if (!isRecord(raw)) return null;
  const knocksImported = int(raw.knocks_imported);
  const doorsTouched = int(raw.doors_touched);
  if (knocksImported === null || doorsTouched === null) return null;
  return {
    knocksImported,
    doorsTouched,
    duplicatesMerged: int(raw.duplicates_merged) ?? 0,
    doorsRecolored: int(raw.doors_recolored) ?? 0,
    rowsSkipped: int(raw.rows_skipped) ?? 0,
    doors: int(raw.doors) ?? 0,
    repsToNotify: int(raw.reps_to_notify) ?? 0,
    runs: parseActivationRuns(raw.runs),
  };
}

export const canApply = (p: ActivationPreview): boolean => p.knocksImported > 0;

const n = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

/** What applying would do, one sentence per fact, in the order a person checks them. */
export function previewLines(p: ActivationPreview): string[] {
  if (!canApply(p)) return [ACTIVATION_COPY.nothingToImport];
  const one = p.knocksImported === 1;
  const lines = [
    `${n(p.knocksImported, 'pin status change', 'pin status changes')} ${one ? 'becomes a Knock' : 'become Knocks'} on ${n(p.doorsTouched, 'Door', 'Doors')}, marked imported and unverified. ${one ? 'It never scores or counts' : 'They never score or count'} toward pay.`,
  ];
  if (p.duplicatesMerged > 0) {
    lines.push(`${n(p.duplicatesMerged, 'duplicate pin merges its', 'duplicate pins merge their')} history into the Door ${p.duplicatesMerged === 1 ? 'it duplicates' : 'they duplicate'}.`);
  }
  lines.push(p.doorsRecolored === 0
    ? 'No Door changes color.'
    : `${n(p.doorsRecolored, 'Door changes', 'Doors change')} color to match ${p.doorsRecolored === 1 ? 'its' : 'their'} latest history.`);
  if (p.rowsSkipped > 0) {
    lines.push(`${n(p.rowsSkipped, 'status change is', 'status changes are')} skipped: no rep or no Disposition recorded.`);
  }
  lines.push(p.repsToNotify > 0
    ? `${n(p.repsToNotify, 'person', 'people')} with Knock Mode will be told the team now sees Door colors.`
    : ACTIVATION_COPY.alreadyTold);
  lines.push(ACTIVATION_COPY.notesPrivate);
  return lines;
}

export function runLine(r: ActivationRun): string {
  const parts = [`${n(r.knocksImported, 'Knock', 'Knocks')} on ${n(r.doorsTouched, 'Door', 'Doors')}`];
  if (r.duplicatesMerged > 0) parts.push(`${r.duplicatesMerged} duplicate${r.duplicatesMerged === 1 ? '' : 's'} merged`);
  if (r.revertedAt) {
    parts.push(`undone, ${n(r.knocksRemoved ?? 0, 'Knock', 'Knocks')} removed`);
  } else if (r.runByName) {
    parts.push(`by ${r.runByName}`);
  }
  return parts.join(' · ');
}
